<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {Ban, Check} from "@lucide/vue";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import {Button} from "@/core/components/ui/button";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import {Textarea} from "@/core/components/ui/textarea";
import {Switch} from "@/core/components/ui/switch";
import {Popover, PopoverContent, PopoverTrigger} from "@/core/components/ui/popover";
import {ColorPicker} from "@/core/components/ui/color-picker";
import CatalogIcon from "@/core/components/icon/CatalogIcon.vue";
import DurationStepper from "@/agenda/components/DurationStepper.vue";
import {AppointmentType} from "@/core/interfaces/AppointmentType";
import {AppointmentTypeStoreRequest} from "@/core/http/resources/AppointmentTypeResource";
import {
    APPOINTMENT_COLORS,
    APPOINTMENT_ICONS,
    DEFAULT_APPOINTMENT_COLOR
} from "@/agenda/constants";

const props = defineProps<{
    type?: AppointmentType | null;
    loading?: boolean;
}>();

const emits = defineEmits<{
    (e: 'submit', request: AppointmentTypeStoreRequest): void;
}>();

const open = defineModel<boolean>('open', {required: true});

const name = ref('');
const duration = ref(30);
const color = ref(DEFAULT_APPOINTMENT_COLOR);
/**
 * Null is a real choice, "no icon", and the starting point of a new type.
 */
const icon = ref<string | null>(null);
const isBookableOnline = ref(false);
const defaultDescription = ref('');

const isEditing = computed(() => !!props.type);

/**
 * The name and the default description of an Apodis template are managed by
 * Apodis: the API refuses them, so the fields are not offered.
 */
const isTemplate = computed(() => !!props.type?.is_global);

const canSubmit = computed(() => (isTemplate.value || name.value.trim().length > 0) && !props.loading);

const description = computed(() => {
    if (!props.type) {
        return 'Créez un type de rendez-vous pour votre agenda.';
    }

    if (isTemplate.value) {
        return `Modifiez la durée ou l'apparence de « ${props.type.name} » pour votre pharmacie.`;
    }

    return 'Modifiez le nom, la description par défaut, la durée ou l\'apparence de ce type de rendez-vous.';
});

/**
 * Case-insensitive hex color comparison (the backend stores uppercase).
 */
const sameColor = (a?: string | null, b?: string | null) => {
    return (a ?? '').toLowerCase() === (b ?? '').toLowerCase();
};

/**
 * An off-palette color, picked here or saved before, is carried by the
 * "other color" swatch, which then shows it as selected.
 */
const isCustomColor = computed(() => !APPOINTMENT_COLORS.some((option) => sameColor(option, color.value)));

const OTHER_COLOR_BACKGROUND = 'conic-gradient(#f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)';

/**
 * Icon choices to render: the backend accepts any icon, so an edited type whose
 * icon is off-list gets it prepended — otherwise nothing would appear selected
 * and a click would silently replace the original value.
 */
const iconChoices = computed(() => {
    const current = props.type?.icon;

    if (current && !APPOINTMENT_ICONS.includes(current)) {
        return [current, ...APPOINTMENT_ICONS];
    }

    return APPOINTMENT_ICONS;
});

/**
 * Reset the form from the edited type (or defaults) each time it opens.
 */
watch(open, (isOpen) => {
    if (!isOpen) {
        return;
    }

    name.value = props.type?.name ?? '';
    duration.value = props.type?.duration ?? 30;
    color.value = props.type?.color ?? DEFAULT_APPOINTMENT_COLOR;
    icon.value = props.type?.icon ?? null;
    isBookableOnline.value = props.type?.is_bookable_online ?? false;
    defaultDescription.value = props.type?.default_description ?? '';
});

const handleSubmit = () => {
    if (!canSubmit.value) {
        return;
    }

    emits('submit', {
        name: name.value.trim(),
        duration: duration.value,
        color: color.value,
        icon: icon.value,
        is_bookable_online: isBookableOnline.value,
        ...(isTemplate.value ? {} : {default_description: defaultDescription.value.trim() || null})
    });
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="max-w-md">
      <DialogHeader>
        <DialogTitle>
          {{ isEditing ? 'Modifier le type de rendez-vous' : 'Nouveau type de rendez-vous' }}
        </DialogTitle>
        <DialogDescription>
          {{ description }}
        </DialogDescription>
      </DialogHeader>
      <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
        <div v-if="!isTemplate" class="flex flex-col gap-1.5">
          <Label for="appointment-type-name">Nom</Label>
          <Input
            id="appointment-type-name"
            v-model="name"
            placeholder="Ex : Vaccination grippe"
            maxlength="255"
          />
        </div>
        <div v-if="!isTemplate" class="flex flex-col gap-1.5">
          <Label for="appointment-type-default-description">Description par défaut</Label>
          <Textarea
            id="appointment-type-default-description"
            v-model="defaultDescription"
            placeholder="Ex : N'oubliez pas votre carnet de santé, carte Vitale et mutuelle"
            maxlength="255"
          />
          <p class="text-xs text-gray-500">
            Insérée dans la description du rendez-vous au choix de ce type, modifiable. Visible du patient.
          </p>
        </div>
        <div class="flex flex-col gap-1.5">
          <Label>Durée par défaut</Label>
          <DurationStepper v-model="duration" />
        </div>
        <div class="flex flex-col gap-1.5">
          <Label>Couleur</Label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="colorOption in APPOINTMENT_COLORS"
              :key="colorOption"
              type="button"
              class="flex size-7 items-center justify-center rounded-full transition-transform hover:scale-110"
              :style="{backgroundColor: colorOption}"
              :aria-label="`Choisir la couleur ${colorOption}`"
              @click="color = colorOption"
            >
              <Check v-if="sameColor(color, colorOption)" class="size-4 text-white" />
            </button>
            <Popover>
              <PopoverTrigger as-child>
                <button
                  type="button"
                  class="flex size-7 items-center justify-center rounded-full transition-transform hover:scale-110"
                  :style="{background: isCustomColor ? color : OTHER_COLOR_BACKGROUND}"
                  aria-label="Choisir une autre couleur"
                  title="Autre couleur"
                >
                  <Check v-if="isCustomColor" class="size-4 text-white" />
                </button>
              </PopoverTrigger>
              <PopoverContent class="w-64">
                <ColorPicker v-model="color" />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div class="flex flex-col gap-1.5">
          <Label>Icône</Label>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="flex size-9 items-center justify-center rounded-lg border transition-colors"
              :class="icon === null ? 'border-primary bg-primary-lightest' : 'border-slate-200 hover:bg-slate-100'"
              aria-label="Aucune icône"
              title="Aucune icône"
              @click="icon = null"
            >
              <Ban class="size-5 text-gray-400" />
            </button>
            <button
              v-for="iconOption in iconChoices"
              :key="iconOption"
              type="button"
              class="flex size-9 items-center justify-center rounded-lg border transition-colors"
              :class="icon === iconOption ? 'border-primary bg-primary-lightest' : 'border-slate-200 hover:bg-slate-100'"
              :aria-label="`Choisir l'icône ${iconOption}`"
              @click="icon = iconOption"
            >
              <CatalogIcon :name="iconOption" class="size-5" />
            </button>
          </div>
        </div>
        <label class="flex items-center justify-between gap-2 text-sm">
          Réservable en ligne
          <Switch v-model="isBookableOnline" />
        </label>
        <DialogFooter>
          <Button type="button" variant="outline" @click="open = false">
            Annuler
          </Button>
          <Button type="submit" class="bg-primary" :disabled="!canSubmit">
            {{ isEditing ? 'Enregistrer' : 'Créer' }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
